/*
 * Copyright (c) 2023 Bolmate B.V.
 */
import './style/step.scss';

import React, {MouseEventHandler, useContext, useEffect, useRef, useState} from 'react';
import {
    AwesomeIcon, ElementPosition, Link, Loader, MenuPlacement, OptionsMenu, OptionsMenuItem, ResponseData,
    SelectItemShape, showDialog, showSnackbar
} from '@dvrd/dvr-controls';
import EmailTemplate from '../../../models/emailTemplateModel';
import EmailStep, {EmailStepFields} from '../../../models/emailStepModel';
import AttachmentSelectController from '../attachment/attachmentSelectController';
import {
    customerTypeItems, getAttachmentsLabel, getDayItems, getProductsLabel, getTemplateLabel
} from '../../../util/emailStepUtil';
import {BolmateInput, BolmateSelect} from '../../controls';
import {AccessKey, ActivateHandle, EmailStatLoader, EmailStepStatTotal, Fulfilment} from '../../../util/interfaces';
import classNames from 'classnames';
import {createErrorMessage, numberTrim, onNavigate} from '../../../util/utils';
import {onShowConfirmSnackbar} from '../../../util/renderUtil';
import DaysSelectController from '../day/daysSelectController';
import {AppContext} from '../../misc/appContext';
import {translateEmailStepError} from '../../../util/bolValidation';
import {hasAdminLogin} from '../../../util/adminUtil';
import CountrySelectController from '../../controls/country/select/countrySelect/countrySelectController';
import TestMailController, {TestMailRef} from '../test/testMailController';
import EmailAdminInfoController from './adminInfo/emailAdminInfoController';
import BulkEmailProducts from './bulkProducts/emailProducts';
import useRight from '../../misc/useRight';
import ProductSelectController, {ProductSelectHandle} from '../../controls/productSelect/productSelectController';
import {EMAIL_SETTINGS_ROUTE} from '../../../util/constants';

interface Props {
    onReplaceStep: (step: EmailStep) => void;
    onDeleteStep?: (step: EmailStep) => void;
    onOpenStats?: (step: EmailStep) => MouseEventHandler;
    onGlobalLoad?: (loading: boolean) => void;
    onClickTemplate: MouseEventHandler;
    allowChange?: (boolOnly?: boolean) => boolean;
    onNavigateTemplate?: (template: EmailTemplate, step: EmailStep, returnPage?: string) => void;
    onLoadStepStats?: (loader: EmailStatLoader) => void;
    step: EmailStep;
    templates: EmailTemplate[];
    index?: number;
    showWarning: boolean;
    className?: string;
    dayItems?: SelectItemShape[];
    error?: string | null;
    productsCount: number;
}

const fulfilmentItems: SelectItemShape[] = [
    {label: 'Alle logistiek', value: Fulfilment.BOTH},
    {label: 'Eigen logistiek', value: Fulfilment.FBR},
    {label: 'LVB', value: Fulfilment.FBB}
];

export default function Step(props: Props) {
    const context = useContext(AppContext);
    const [canEdit, canTestMail, canOpenStats] = useRight(AccessKey.EMAIL_EDIT, AccessKey.EMAIL_TEST_MAIl,
        AccessKey.EMAIL_STATS);
    const {
        step, onDeleteStep, onReplaceStep, templates, index, onOpenStats, showWarning, className,
        dayItems, onClickTemplate, error, onGlobalLoad, onNavigateTemplate, onLoadStepStats, productsCount
    } = props;
    const [loading, setLoading] = useState(false);
    const [loadingStats, setLoadingStats] = useState(true);
    const [stats, setStats] = useState<EmailStepStatTotal>({CLICK: 0, SENT: 0, OPEN: 0});
    const [editingLabel, setEditingLabel] = useState(false);
    const attachmentRef = useRef<ActivateHandle>(null);
    const productRef = useRef<ProductSelectHandle>(null);
    const daysRef = useRef<ActivateHandle>(null);
    const countryRef = useRef<ActivateHandle>(null);
    const testMailRef = useRef<TestMailRef>(null);
    const bulkProductsRef = useRef<ActivateHandle>(null);
    const allowChange = props.allowChange ?? function () {
        return true;
    };

    function onEditLabel() {
        setEditingLabel(true);
    }

    function onChangeLabel(value: string) {
        step.label = value;
        onReplaceStep(step);
    }

    function onSubmitLabel() {
        setLoading(true);
        step.update((step: EmailStep, success: boolean, data: ResponseData) => {
            setLoading(false);
            if (success) {
                onReplaceStep(step);
                setEditingLabel(false);
            } else
                showDialog(createErrorMessage(data.message ?? 'Het bijwerken van de e-mail is niet gelukt.'),
                    'Bijwerken mislukt');
        });
    }

    function onClickCountries() {
        if (allowChange())
            countryRef.current?.activate(step.countries);
    }

    function onClickAttachment() {
        if (allowChange())
            attachmentRef.current?.activate(step);
    }

    function onClickProduct() {
        if (!allowChange()) return;
        const selectedEANs = step.productEans;
        productRef.current?.activate({selectedEANS: selectedEANs});
    }

    function onChangeDays(value: string | number) {
        if (!allowChange()) return;
        if (value === 'custom') return daysRef.current?.activate(step);
        else if (typeof value === 'number') onChangeStep('days')(value);
    }

    function onCustomDays(days: number, onlyWorkDays: boolean) {
        if (!allowChange()) return;
        step.days = days;
        step.onlyWorkDays = onlyWorkDays;
        setLoading(true);
        step.update((step: EmailStep, success: boolean, data: ResponseData) => {
            if (success) {
                setLoading(false);
                onReplaceStep(step);
                onShowConfirmSnackbar('De e-mail is bijgewerkt');
            } else showDialog(createErrorMessage(data.message ?? 'Het bijwerken van de e-mail is niet gelukt.'),
                'Bijwerken mislukt');
        });
    }

    function onChangeStep(key: keyof EmailStepFields) {
        return function (value: any) {
            if (!allowChange()) return;
            step.setField(key, value);
            setLoading(true);
            step.update((step: EmailStep, success: boolean, data: ResponseData) => {
                setLoading(false);
                if (success) {
                    onReplaceStep(step);
                    onShowConfirmSnackbar('De e-mail is bijgewerkt');
                } else showDialog(createErrorMessage(data.message ?? 'Het bijwerken van de e-mail is niet gelukt.'),
                    'Bijwerken mislukt');
            });
        };
    }

    function onChangeCountries(countries: Set<string>, allSelected: boolean, callback: (close: boolean) => void) {
        if (!allowChange()) return;
        if (allSelected) step.countries = [];
        else step.countries = Array.from(countries);
        setLoading(true);
        step.update((step: EmailStep, success: boolean, data: ResponseData) => {
            setLoading(false);
            if (success) {
                onReplaceStep(step);
                onShowConfirmSnackbar('De e-mail is bijgewerkt');
            } else
                showDialog(createErrorMessage(data.message ?? 'Het bijwerken van de e-mail is niet gelukt.'),
                    'Bijwerken mislukt');
            callback(success);
        });
    }

    function onChangeProducts(selectedEANs: Array<string>) {
        if (!allowChange()) return productRef.current?.close();
        if (!step.eansDiffer(selectedEANs)) return productRef.current?.close();
        step.productEans = selectedEANs;
        setLoading(true);
        step.update((step: EmailStep, success: boolean, data: ResponseData) => {
            setLoading(false);
            if (success) {
                onReplaceStep(step);
                onShowConfirmSnackbar('De e-mail is bijgewerkt');
            } else
                showDialog(createErrorMessage(data.message ?? 'Het bijwerken van de e-mail is niet gelukt.'),
                    'Bijwerken mislukt');
            productRef.current?.close();
        });
    }

    function onClickEdit() {
        if (!allowChange()) return;
        if (onNavigateTemplate && step.template) return onNavigateTemplate(step.template, step);
        if (!step.template) showDialog(createErrorMessage('Je hebt nog geen e-mail template gekozen.'));

    }

    function onClickTestSend() {
        if (!step.template) return showDialog(createErrorMessage('Je hebt nog geen e-mail template gekozen.'));
        testMailRef.current?.activate();
    }

    function onCloseTestAddress() {
        testMailRef.current?.onClose();
    }

    function onSendTestMail(recipient: string) {
        if (!step.template) return;
        testMailRef.current?.setLoading(true);
        step.template.test(recipient, step.id, (_: EmailTemplate, success: boolean, data: ResponseData) => {
            testMailRef.current?.setLoading(false);
            if (success) {
                onCloseTestAddress();
                showSnackbar('De testmail is verzonden');
            } else showDialog(createErrorMessage(data.message ?? 'Het verzenden van de testmail is niet gelukt.'),
                'Verzenden mislukt');
        });
    }

    function getStepOptionsMenu(): OptionsMenuItem[] {
        const items: OptionsMenuItem[] = [];
        if (canEdit) {
            items.push(
                {
                    label: 'Naam wijzigen',
                    onClick: onEditLabel,
                    icon: <AwesomeIcon name='edit' className='icon'/>
                },
                {
                    label: 'Producten koppelen',
                    onClick: onClickBulkProducts,
                    icon: <AwesomeIcon name='link' className='icon'/>
                }
            );
            if (props.onNavigateTemplate !== undefined)
                items.push({
                    label: 'Inhoud wijzigen',
                    onClick: onClickEdit,
                    icon: <AwesomeIcon name='pen' className='icon'/>
                });
        }
        if (canTestMail)
            items.push({
                label: 'Test verzenden',
                onClick: onClickTestSend,
                icon: <AwesomeIcon name='paper-plane' className='icon'/>
            });
        if (onDeleteStep && canEdit) items.push({
            label: 'Verwijderen',
            onClick: onClickDelete,
            icon: <AwesomeIcon name='trash' className='icon'/>
        });
        return items;
    }

    function onClickBulkProducts() {
        if (!allowChange()) return;
        bulkProductsRef.current?.activate(step);
    }

    function onClickDelete() {
        showDialog('Weet je zeker dat je deze e-mail wilt verwijderen?', 'E-mail verwijderen',
            ['Nee', {label: 'Ja', onClick: onConfirmDelete, primary: false}]);
    }

    function onConfirmDelete() {
        if (!onDeleteStep) return;
        if (onGlobalLoad) onGlobalLoad(true);
        setLoading(true);
        step.delete((step: EmailStep, success: boolean, data: ResponseData) => {
            if (onGlobalLoad) onGlobalLoad(false);
            setLoading(false);
            if (success) {
                showSnackbar('De e-mail is verwijderd');
                onDeleteStep(step);
            } else showDialog(createErrorMessage(data.message ?? 'Het verwijderen van de stap is niet gelukt.'),
                'Verwijderen mislukt');
        });
    }

    function renderLabel() {
        // TODO Show edit on hover, full title clickable
        if (editingLabel)
            return renderLabelEditor();
        return (
            <label className='step-title' onClick={onEditLabel}>
                {step.label}
                {renderIsNew(isNew)}
                <AwesomeIcon name='edit' className='edit-icon' title='Naam wijzigen'/>
            </label>
        );
    }

    function renderLabelEditor() {
        return (
            <BolmateInput onChange={onChangeLabel} value={step.label} onEnter={onSubmitLabel} className='label-editor'
                          ornaments={{
                              element: <AwesomeIcon name='check' onClick={onSubmitLabel} className='submit-check'/>,
                              placement: ElementPosition.RIGHT
                          }} autoFocus autoSelect/>
        );
    }

    function renderIndex() {
        if (!index) return null;
        return (
            <>
                {index > 1 && <div className='line top'/>}
                <div className='line bottom'/>
                <div className='index-container'>
                    <span className='index'>{index}</span>
                </div>
            </>
        );
    }

    function renderVarButton(label: string, onClick: MouseEventHandler, isRequired: boolean = false) {
        const clickable = !loading && canEdit;
        return (
            <div className={classNames('var-button', isRequired && 'required', loading && 'disabled',
                !canEdit && 'forbidden')} onClick={clickable ? onClick : undefined}>
                <label className='button-label'>{label}</label>
            </div>
        );
    }

    function renderDaysSection() {
        const selectValue = [-1, 0, 1, 3, 7, 14].includes(step.days) ? step.days : 'custom',
            items = dayItems ?? getDayItems(step, context.accountContext.account);
        return (
            <div className='section'>
                <label className='text'>Na</label>
                <BolmateSelect value={selectValue} onChange={onChangeDays} items={items}
                               className={classNames('select-field', !canEdit && 'forbidden')}
                               canOpen={allowChange} disabled={loading || !canEdit}/>
            </div>
        );
    }

    function renderProductsSection() {
        const isRequired = !step.productEans.length;
        return (
            <div className='section'>
                <label className='text'>voor</label>
                {renderVarButton(getProductsLabel(step, productsCount), onClickProduct, isRequired)}
            </div>
        );
    }

    function renderTemplateSection() {
        const isRequired = step.templateId === '';
        return (
            <div className='section'>
                <label className='text'>stuur</label>
                {renderVarButton(getTemplateLabel(step, templates), onClickTemplate, isRequired)}
            </div>
        );
    }

    function renderAttachmentSection() {
        return (
            <div className='section'>
                <label className='text'>mail met bijlage</label>
                {renderVarButton(getAttachmentsLabel(step), onClickAttachment)}
            </div>
        );
    }

    function renderCustomerSection() {
        return (
            <div className='section'>
                <label className='text'>naar</label>
                <BolmateSelect value={step.customerType} onChange={onChangeStep('customer_type')}
                               items={customerTypeItems} className={classNames('select-field', !canEdit && 'forbidden')}
                               canOpen={allowChange} disabled={loading || !canEdit}/>
            </div>
        );
    }

    function renderCountry() {
        const count = step.countries.length;
        let label: string;
        if (!count) label = 'Alle landen';
        else if (count === 1) label = step.countries[0];
        else label = `${count} landen`;
        return (
            <div className='section'>
                <label className='text'>in</label>
                {renderVarButton(label, onClickCountries)}
            </div>
        );
    }

    function renderFulfilment() {
        return (
            <div className='section'>
                <label className='text'>via</label>
                <BolmateSelect value={step.fulfilment} onChange={onChangeStep('fulfilment')}
                               items={fulfilmentItems} className={classNames('select-field', !canEdit && 'forbidden')}
                               canOpen={allowChange} disabled={loading || !canEdit}/>
            </div>
        );
    }

    function loadStats() {
        if (!onOpenStats || !onLoadStepStats) return;
        setLoadingStats(true);
        onLoadStepStats({
            id: step.id, callback: (data: ResponseData) => {
                setLoadingStats(false);
                if (data.success) setStats({
                    CLICK: data.click_count, SENT: data.sent_count, OPEN: data.open_count
                });
                else showDialog(createErrorMessage(data.message ?? 'Het laden van de statistieken is niet gelukt.'),
                    'Laden mislukt');
            }
        });
    }

    function renderStatsButton() {
        const {SENT, OPEN, CLICK} = stats;
        if (!onOpenStats) return;
        return (
            <div className={classNames('email-stats-button', !canOpenStats && 'forbidden')}
                 onClick={canOpenStats ? onOpenStats(step) : undefined}>
                <Loader active={loadingStats} size='3rem'/>
                <div className='stat-container'>
                    <label>Verstuurd</label>
                    <label>{loadingStats ? '-' : numberTrim(SENT)}</label>
                </div>
                <AwesomeIcon name='arrow-up-right-from-square' className='icon-container'/>
                <div className='stat-container'>
                    <label>Open</label>
                    <label>{loadingStats ? '-' : (SENT === 0 ? 0 : (OPEN / SENT * 100).toFixed(1))}%</label>
                </div>
                <div className='stat-container'>
                    <label>Klik</label>
                    <label>{loadingStats ? '-' : numberTrim(CLICK)}</label>
                </div>
            </div>
        );
    }

    function renderStats() {
        if (!onOpenStats) return null;
        const isAdmin = context.userContext.user?.isAdmin() === true || hasAdminLogin();
        const statsDisabled = !isAdmin && context.accountContext.account?.getFlex('use_no_reply_tracking') !== true;
        return (
            <div className={classNames('step-info', showWarning && 'with-warning')}>
                {statsDisabled ?
                    <div className='stats-disabled' onClick={onNavigate(EMAIL_SETTINGS_ROUTE)}>
                        <label className='text'>Statistieken staan uit...</label>
                    </div>
                    :
                    renderStatsButton()
                }
                {renderWarn()}
            </div>
        );
    }

    function renderWarn() {
        if (!showWarning) return;
        let _error: string | null;
        if (error) _error = translateEmailStepError(error, false);
        else _error = 'Er wordt al een andere e-mail naar deze klanten verzonden. Stuur maximaal 1 mail per 1 order.';
        return (
            <div className='step-warning'>
                <AwesomeIcon name='exclamation-circle' className='warn-icon'/>
                <p className='description'>{_error} <Link
                    route='https://partnerplatform.bol.com/nl/hulp-nodig/prestaties/beleidspunten-zelfpromotie/'>Meer
                    informatie</Link></p>
            </div>
        );
    }

    function renderQuickActions() {
        return (
            <OptionsMenu options={getStepOptionsMenu()} placement={MenuPlacement.BOTTOM_LEFT}
                         optionsComponent={<AwesomeIcon name='gear' className='step-options-icon icon'/>}
                         containerClass='common-options'/>
        );
    }

    function renderIsNew(isNew: boolean) {
        if (!isNew) return null;
        return <span className='new-email-tag'>Nieuw</span>;
    }

    useEffect(() => {
        loadStats();
    }, [step.id]);

    const isNew = step.getFlex('new') === true;
    return (
        <>
            <div className={classNames('email-step', className)}>
                <EmailAdminInfoController emailStepID={step.id}/>
                {renderLabel()}
                {renderIndex()}
                <div className={classNames('step-settings', isNew && 'new')}>
                    <Loader active={loading} size='3rem'/>
                    {renderDaysSection()}
                    {renderProductsSection()}
                    {renderTemplateSection()}
                    {renderAttachmentSection()}
                    {renderCustomerSection()}
                    {renderCountry()}
                    {renderFulfilment()}
                </div>
                {renderStats()}
                {renderQuickActions()}
            </div>
            <AttachmentSelectController onReplaceStep={onReplaceStep} ref={attachmentRef}/>
            <ProductSelectController onSubmit={onChangeProducts} ref={productRef} loading={loading}/>
            {/*<ProductSelectController onSubmit={onChangeProducts} ref={productRef}/>*/}
            <DaysSelectController onSubmit={onCustomDays} ref={daysRef}/>
            <TestMailController onSubmit={onSendTestMail} ref={testMailRef} closeOnSubmit={false}/>
            <CountrySelectController ref={countryRef} onSubmit={onChangeCountries} loading={loading}
                                     splitBelgiumLanguage/>
            <BulkEmailProducts ref={bulkProductsRef} onChangeProducts={onChangeProducts}/>
        </>
    );
}